import { createApi } from "@reduxjs/toolkit/query/react";
import api from "./api";

// Reuse Axios: preserve cookies, locale headers and auth error handling.
const axiosBaseQuery = async ({ url, method = "get", data }, { signal }) => {
  try {
    const response = await api({ url, method, data, signal });
    return { data: response.data?.data };
  } catch (error) {
    return {
      error: {
        status: error.response?.status || "FETCH_ERROR",
        data: error.response?.data || error.message,
      },
    };
  }
};

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: axiosBaseQuery,
  tagTypes: ["Blogs", "Users", "Permissions"],
  keepUnusedDataFor: 120,
  refetchOnMountOrArgChange: 60,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getPermissions: builder.query({
      query: () => ({ url: "/users/permissions" }),
      providesTags: ["Permissions"],
    }),
    getBlogTypes: builder.query({
      query: () => ({ url: "/blogTypes" }),
    }),
    getRoles: builder.query({
      query: () => ({ url: "/users/roles" }),
    }),
    getUsers: builder.query({
      query: () => ({ url: "/users" }),
      providesTags: ["Users"],
    }),
    getBlogs: builder.query({
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        const [all, featured] = await Promise.all([
          baseQuery({ url: "/blogs" }),
          baseQuery({ url: "/blogs/featured" }),
        ]);
        if (all.error) return { error: all.error };
        if (featured.error) return { error: featured.error };

        const featuredIds = new Set(
          (Array.isArray(featured.data) ? featured.data : []).map(
            (blog) => String(blog.id),
          ),
        );
        const blogs = (Array.isArray(all.data) ? all.data : [])
          .map((blog) => ({ ...blog, featured: featuredIds.has(String(blog.id)) }))
          .sort((a, b) => Number(b.featured) - Number(a.featured));
        return { data: blogs };
      },
      providesTags: ["Blogs"],
    }),
  }),
});

export const {
  useGetPermissionsQuery,
  useGetBlogTypesQuery,
  useGetRolesQuery,
  useGetUsersQuery,
  useGetBlogsQuery,
} = adminApi;
